import { CreativeCharacterEditAction } from "./CreativeCharacterEditAction";
import { CharacterAssignmentDialog } from "./CharacterAssignmentDialog";
import { AdLayoutEditor } from "./AdLayoutEditor";
import { CreativeFilingEditor } from "./CreativeFilingEditor";
import { CreativeProvenance } from "@/components/creative-materials/CreativeProvenance";
import type { CreativeItem, CreativeUsage } from "@digital-twins/shared-types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  Share2,
  Trash2,
  AlertCircle,
  Pencil,
  Loader2,
} from "@digital-twins/design-system/icons";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { Badge } from "@/components/ui/badge";
import { CollectionShareDialog } from "@/components/library/CollectionShareDialog";
import { useAuth } from "@/lib/config/auth-provider";
import { useCreativeShareAdapter } from "@/hooks/useCreativeShareAdapter";
import { usePrivateMedia } from "@/hooks/usePrivateMedia";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { CreativeDetailProps } from "@/types/creative-library";
import { useDarkImageTile, withDarkTile } from "./imageTone";

const DETAIL_IMAGE_CLASS =
  "max-h-[34dvh] w-full object-contain sm:max-h-[52dvh]";

export function CreativeDetail({
  item,
  brands,
  products,
  subjects = [],
  subjectsReady = true,
  onClose,
  onSaved,
  onOpenSubject,
}: CreativeDetailProps) {
  const { t } = useTranslation("app");
  const { user } = useAuth();
  const [composing, setComposing] = useState(false);
  const [adSource, setAdSource] = useState<CreativeItem | null>(null);
  const [adLogos, setAdLogos] = useState<CreativeItem[]>([]);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const assignmentChanged = useRef(false);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<"save" | "download" | null>(null);
  const [downloading, setDownloading] = useState(false);
  const media = usePrivateMedia(item?.mediaUrl);
  const { hasDarkTile, onLoad: onImageLoad } = useDarkImageTile(media.src);
  const shareAdapter = useCreativeShareAdapter(
    item?.isOwner ? item.token : null,
    user?.organizationToken,
  );
  async function createAd() {
    if (!item) return;
    setLoadingLayout(true);
    setError(null);
    try {
      const current = await creativeLibraryApi.creative(item.token);
      const available = await creativeLibraryApi.creatives(
        current.brandToken ?? undefined,
      );
      setAdSource(current);
      setAdLogos(available.filter((reference) => reference.role === "logo"));
      setComposing(true);
    } catch {
      setError("save");
    } finally {
      setLoadingLayout(false);
    }
  }
  async function updateUsage(usage: CreativeUsage) {
    if (!item || !item.canEdit) return;
    setUpdating(true);
    setError(null);
    try {
      await creativeLibraryApi.updateCreative(item.token, {
        revision: item.revision,
        usage,
      });
      onSaved();
    } catch {
      setError("save");
    } finally {
      setUpdating(false);
    }
  }
  async function downloadOriginal() {
    if (!item) return;
    setDownloading(true);
    setError(null);
    try {
      const original = await creativeLibraryApi.downloadCreative(item.token);
      const url = URL.createObjectURL(
        new Blob([original], { type: "application/octet-stream" }),
      );
      const extensions: Record<string, string> = {
        "image/svg+xml": "svg",
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/webp": "webp",
      };
      const extension = item.mimeType ? extensions[item.mimeType] : undefined;
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = extension
        ? `${item.title.replace(/\.(svg|png|jpe?g|webp)$/i, "")}.${extension}`
        : item.title;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError("download");
    } finally {
      setDownloading(false);
    }
  }
  async function remove() {
    if (!item) return;
    setDeleting(true);
    setError(null);
    try {
      await creativeLibraryApi.archive("creatives", item.token);
      onSaved();
      onClose();
    } catch {
      setError("save");
    } finally {
      setDeleting(false);
    }
  }
  return (
    <>
      <CreativeEditorDialog
        open={Boolean(item) && !sharing && !editing && !composing && !assigning}
        busy={updating || deleting || downloading || loadingLayout}
        onClose={onClose}
        kind="creatives"
        size="preview"
        title={item?.title ?? ""}
        description={[
          brands.find((brand) => brand.token === item?.brandToken)?.name ??
            t("creativeLibrary.noBrand"),
          products.find((product) => product.token === item?.productToken)
            ?.name,
        ]
          .filter(Boolean)
          .join(" · ")}
        testId="creative-detail"
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex w-full items-center justify-between gap-1 sm:w-auto sm:gap-2">
              {item?.isOwner && (
                <Button
                  variant="ghost"
                  disabled={deleting}
                  onClick={() => void remove()}
                  data-testid="creative-delete"
                >
                  <Trash2 className="h-4 w-4" />
                  {t("creativeLibrary.remove")}
                </Button>
              )}
              {item?.canEdit && item.role === "identity" && onOpenSubject && (
                <CreativeCharacterEditAction
                  item={item}
                  subjects={subjects}
                  disabled={!subjectsReady}
                  onOpen={onOpenSubject}
                  onAssign={() => setAssigning(true)}
                />
              )}
              {item?.canEdit && (
                <Button
                  variant="ghost"
                  onClick={() => setEditing(true)}
                  data-testid="creative-edit-filing"
                >
                  <Pencil className="h-4 w-4" />
                  {t(
                    item.role === "identity"
                      ? "creativeLibrary.editImageDetails"
                      : "creativeLibrary.editFiling",
                  )}
                </Button>
              )}
              {shareAdapter && (
                <Button
                  variant="ghost"
                  onClick={() => setSharing(true)}
                  data-testid="creative-share"
                >
                  <Share2 className="h-4 w-4" />
                  {t("creativeLibrary.share")}
                </Button>
              )}
            </div>
            <div className="flex w-full justify-end gap-2 sm:w-auto">
              <Button
                variant="outline"
                disabled={downloading}
                onClick={() => void downloadOriginal()}
                data-testid="creative-download"
              >
                {downloading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {t("creativeLibrary.download")}
              </Button>
              <Button
                variant="primary"
                disabled={loadingLayout}
                onClick={() => void createAd()}
                data-testid="creative-create-ad"
              >
                {loadingLayout && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("adLayouts.title")}
              </Button>
            </div>
          </div>
        }
      >
        <div className="grid min-w-0 items-start gap-6 sm:grid-cols-2">
          <div className="flex min-h-40 min-w-0 items-center justify-center rounded-xl border border-border bg-background p-3">
            {media.src ? (
              <img
                src={media.src}
                alt={item?.title}
                className={
                  hasDarkTile
                    ? withDarkTile(DETAIL_IMAGE_CLASS)
                    : DETAIL_IMAGE_CLASS
                }
                data-testid="creative-detail-image"
                onLoad={onImageLoad}
              />
            ) : (
              <Text color="muted">
                {t(
                  media.error
                    ? "creativeLibrary.imageUnavailable"
                    : "creativeLibrary.loading",
                )}
              </Text>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-5">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">
                {t(`creativeLibrary.roles.${item?.role ?? "style"}`)}
              </Badge>
              {!item?.canEdit && (
                <Badge variant="outline">
                  {t(`creativeLibrary.usage.${item?.usage ?? "unspecified"}`)}
                </Badge>
              )}
              <Badge variant="outline">
                {t(`creativeLibrary.sharing.${item?.sharingMode ?? "private"}`)}
              </Badge>
            </div>
            {item?.canEdit && item.role === "identity" && (
              <Button
                variant="outline"
                className="self-start"
                onClick={() => setAssigning(true)}
                data-testid="creative-add-character"
              >
                {t("creativeLibrary.addToCharacter")}
              </Button>
            )}
            {item?.isOwner && item.generationProvenance && (
              <CreativeProvenance provenance={item.generationProvenance} />
            )}
            {item?.canEdit && (
              <CreativeEditorSection>
                <Text size="sm" color="muted">
                  {t("creativeLibrary.allowedUsage")}
                </Text>
                <Select
                  value={item.usage}
                  disabled={updating || deleting}
                  onValueChange={(value) =>
                    void updateUsage(value as CreativeUsage)
                  }
                >
                  <SelectTrigger
                    aria-label={t("creativeLibrary.allowedUsage")}
                    data-testid="creative-detail-usage"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      [
                        "unspecified",
                        "internal_only",
                        "external_allowed",
                      ] as const
                    ).map((usage) => (
                      <SelectItem
                        key={usage}
                        value={usage}
                        disabled={
                          usage === "external_allowed" &&
                          Boolean(
                            item.generationProvenance &&
                            item.generationProvenance.usageCeiling !==
                              "external_allowed",
                          )
                        }
                      >
                        {t(`creativeLibrary.usage.${usage}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Text size="xs" color="muted">
                  {t("creativeLibrary.allowedUsageHint")}
                </Text>
                {item.generationProvenance &&
                  item.generationProvenance.usageCeiling !==
                    "external_allowed" && (
                    <Text size="xs" color="muted">
                      {t("creativeLibrary.usageCeilingHint")}
                    </Text>
                  )}
              </CreativeEditorSection>
            )}
            {error && (
              <Text
                size="sm"
                className="flex items-center gap-2 text-destructive"
                role="alert"
              >
                <AlertCircle className="h-4 w-4" />
                {t(
                  error === "download"
                    ? "creativeLibrary.downloadFailed"
                    : "creativeLibrary.saveFailed",
                )}
              </Text>
            )}
          </div>
        </div>
      </CreativeEditorDialog>
      {assigning && item && (
        <CharacterAssignmentDialog
          key={item.token}
          item={item}
          brands={brands}
          onClose={() => {
            setAssigning(false);
            // The reload empties `item`, which would unmount the open dialog and lose its retry state.
            if (assignmentChanged.current) {
              assignmentChanged.current = false;
              onSaved();
            }
          }}
          onSaved={() => {
            assignmentChanged.current = true;
          }}
          onOpenSubject={onOpenSubject}
        />
      )}
      <CreativeFilingEditor
        open={editing}
        item={item}
        brands={brands}
        products={products}
        onClose={() => setEditing(false)}
        onSaved={onSaved}
      />
      {shareAdapter && (
        <CollectionShareDialog
          title={t("creativeLibrary.shareCreative")}
          open={sharing}
          onOpenChange={(open) => {
            setSharing(open);
            if (!open) onSaved();
          }}
          adapter={shareAdapter}
        />
      )}
      {composing && adSource && (
        <AdLayoutEditor
          key={adSource.token}
          source={adSource}
          logos={adLogos}
          onClose={() => setComposing(false)}
        />
      )}
    </>
  );
}
